import type React from "react"
import { useTranslation } from "react-i18next"

import { useFormatCurrency } from "@/formatters/use-format-currency"

import { FormControl, MenuItem, Select, Slider, Stack } from "@mui/material"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"

import { Eyebrow } from "../homepage/atoms"
import type { Confidence } from "../homepage/insights-homepage.types"
import type { ConfidenceFilter, DetectorFilter } from "./filter-action-rows"

const CONFIDENCE_OPTIONS: Confidence[] = ["HIGH", "MEDIUM", "LOW"]

/** One selectable detector: stable `code` value + human-readable `label`. */
export interface DetectorOption {
  code: string
  label: string
}

export interface ActionsFilterBarProps {
  detector: DetectorFilter
  confidence: ConfidenceFilter
  detectors: readonly DetectorOption[]
  minDollars: number
  maxDollars: number
  onDetectorChange: (value: DetectorFilter) => void
  onConfidenceChange: (value: ConfidenceFilter) => void
  onMinDollarsChange: (value: number) => void
}

export const ActionsFilterBar: React.FC<ActionsFilterBarProps> = ({
  detector,
  confidence,
  detectors,
  minDollars,
  maxDollars,
  onDetectorChange,
  onConfidenceChange,
  onMinDollarsChange,
}) => {
  const { t } = useTranslation("common")
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    notation: "compact",
  })
  // Round the slider ceiling up so the max-impact row stays selectable, and pick
  // a readable step relative to the range.
  const ceiling = Math.max(1000, Math.ceil(maxDollars / 1000) * 1000)
  const step = Math.max(100, Math.round(ceiling / 50 / 100) * 100)

  return (
    <Stack
      direction="row"
      spacing={3}
      sx={{ alignItems: "flex-end", flexWrap: "wrap" }}
    >
      <Stack spacing={0.5} sx={{ minWidth: 220 }}>
        <Eyebrow>Detector</Eyebrow>
        <FormControl size="small">
          <Select
            aria-label="Detector"
            onChange={(e) => {
              onDetectorChange(e.target.value)
            }}
            value={detector}
          >
            <MenuItem value="ALL">All detectors</MenuItem>
            {detectors.map((d) => (
              <MenuItem key={d.code} value={d.code}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={0.5}>
        <Eyebrow>Confidence</Eyebrow>
        <ToggleButtonGroup
          color="primary"
          exclusive
          onChange={(_e, value: ConfidenceFilter | null) => {
            onConfidenceChange(value ?? "ALL")
          }}
          size="small"
          value={confidence}
        >
          <ToggleButton value="ALL">All</ToggleButton>
          {CONFIDENCE_OPTIONS.map((level) => (
            <ToggleButton key={level} value={level}>
              {level}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Stack spacing={0.5} sx={{ minWidth: 220, flex: 1, maxWidth: 320 }}>
        <Eyebrow>
          {t("neat.minObserved", { amount: formatCurrency(minDollars) })}
        </Eyebrow>
        <Slider
          aria-label={t("neat.minObservedAriaLabel")}
          max={ceiling}
          min={0}
          onChange={(_e: Event, value: number | number[]) => {
            onMinDollarsChange(Array.isArray(value) ? value[0] : value)
          }}
          size="small"
          step={step}
          value={minDollars}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => formatCurrency(v)}
        />
      </Stack>
    </Stack>
  )
}
